import axios, { AxiosRequestConfig } from "axios";
import { QueryClient } from "@tanstack/react-query";
import { NEXT_PUBLIC_API_BASE_URL, NEXT_PUBLIC_API_INTERNAL_BASE_URL } from "@/consts";
import { getIsAuthorized } from "@/components/login-modal/login-modal";


const BASE_URL =
  typeof window === "undefined"
    ? NEXT_PUBLIC_API_INTERNAL_BASE_URL
    : NEXT_PUBLIC_API_BASE_URL;

const createClient = () => {
  const instance = axios.create({
    baseURL: BASE_URL,
    timeout: 30000,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  });

  // Request interceptor
  instance.interceptors.request.use((config) => {
    if (typeof window !== "undefined") {
      const token = getIsAuthorized();
      if (token) config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  // Response interceptor
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.code === "ECONNABORTED") {
        console.error("Request timeout:", error.config?.url);
      }
      return Promise.reject(error);
    },
  );

  return {
    async request<T>(config: AxiosRequestConfig) {
      try {
        const response = await instance.request<T>({
          ...config,
        });

        return response.data;
      } catch (error) {
        console.error(`Request to ${config.url} failed:`, error);
        throw error;
      }
    },

    get<T>(url: string, config?: AxiosRequestConfig) {
      return this.request<T>({ ...config, method: "GET", url });
    },

    post<T>(url: string, data?: unknown, config?: AxiosRequestConfig) {
      return this.request<T>({ ...config, method: "POST", url, data });
    },

    put<T>(url: string, data?: unknown, config?: AxiosRequestConfig) {
      return this.request<T>({ ...config, method: "PUT", url, data });
    },

    patch<T>(url: string, data?: unknown, config?: AxiosRequestConfig) {
      return this.request<T>({ ...config, method: "PATCH", url, data });
    },

    delete<T>(url: string, config?: AxiosRequestConfig) {
      return this.request<T>({ ...config, method: "DELETE", url });
    },
  };
};

export const httpClient = createClient();

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retryDelay: 100,
      staleTime: 600_000,
      retry: (attempts, error: any) => error?.status !== 401 && attempts < 2,
    },
  },
});
